/**
 * The typed client generated from the API's OpenAPI spec (M0-25).
 *
 * `openapi-fetch` chosen over a heavier generator (Orval, NSwag, Kiota) for
 * the reasoning recorded on the Tech Stack Notion page: it emits no runtime
 * code of its own — `../generated/schema.d.ts` is pure types — which plays
 * cleanly with this project's strict `tsconfig` (`noUncheckedIndexedAccess`,
 * `verbatimModuleSyntax`) and keeps CI's drift diff to one generated file
 * instead of one file per endpoint.
 *
 * Bearer-token attachment goes through the same `authToken` module `apiGet`
 * uses — one place that knows how to get a token, not two.
 */
import createClient from 'openapi-fetch';
import type { paths } from './generated/schema';
import { getCurrentAccessToken } from '@/lib/authToken';
import { reportApiUnavailable, reportAuthRequired } from '@/lib/maintenance';

// NOT `VITE_API_BASE_URL ?? ''`. `apiGet` can get away with an empty base
// because it string-concatenates onto a relative path, which `fetch()`
// resolves against the current page like a browser always has. openapi-fetch
// builds a real `URL` object internally — `new URL('/api/v1/me', '')` throws
// `Invalid URL`, since an empty string isn't a valid base to resolve against.
// `location.origin` reproduces the same "same-origin, relative /api" behavior
// as an actual absolute origin, in both the browser and jsdom (which defaults
// to `http://localhost:3000`).
const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;

export const apiClient = createClient<paths>({
  baseUrl,
  // NOT `fetch: globalThis.fetch` — openapi-fetch defaults `fetch` to
  // `globalThis.fetch` as a parameter evaluated once, at this module's first
  // import, and `apiClient` is a module-level singleton. A test that swaps
  // `globalThis.fetch` afterwards (`vi.stubGlobal`, the pattern `apiGet`'s
  // tests already use) would silently keep hitting the real network. This
  // indirection re-reads `globalThis.fetch` on every call instead.
  fetch: (input) => globalThis.fetch(input),
});

apiClient.use({
  async onRequest({ request }) {
    let token: string | null;
    try {
      token = await getCurrentAccessToken();
    } catch (error) {
      // openapi-fetch does not wrap `onRequest` in try/catch, so a throw here
      // (a network blip reaching Entra, a clock-skew `ServerError` — anything
      // `getApiAccessToken` does not treat as `InteractionRequiredAuthError`)
      // would otherwise skip the onResponse/onError middleware below entirely
      // instead of being reported.
      reportApiUnavailable();
      throw error;
    }
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
});

// The proxy/gateway statuses, never something OUR code returns on purpose —
// NOT 503, which `JobPhotoEndpoints`, `AddressEndpoints`, `MapsEndpoints`, and
// `TechnicianProfileEndpoints` all hand-write deliberately for a scoped
// feature being unavailable (storage, geocoding, map tiles not configured),
// so treating it as a platform outage would flag those as maintenance too.
// Nothing in this API hand-writes a 502 or a 504. Confirmed against the
// actual local dev proxy: with the API not running, Vite's own proxy answers
// `/api/*` with a 502, not a network-level failure — and Azure Static Web
// Apps' reverse proxy does the same thing in front of a torn-down Container
// App (M0-09). A thrown fetch (`onError`, below) is the OTHER half of
// "unreachable": no proxy in front at all, or the proxy itself unreachable.
const GATEWAY_STATUSES = new Set([502, 504]);

// M1-29: reports what the maintenance detector needs, never handles the
// response itself — every call site still sees its own error/status exactly
// as before. A 401 goes to `reportAuthRequired` (routes to sign-in, never
// maintenance); a network-level failure or a gateway status goes to
// `reportApiUnavailable`, which confirms via `/health` before showing
// anything (see `lib/maintenance.ts` for why one bad response is not enough
// on its own). A business 500 deliberately reports nothing.
apiClient.use({
  onResponse({ response }) {
    if (response.status === 401) {
      reportAuthRequired();
    } else if (GATEWAY_STATUSES.has(response.status)) {
      reportApiUnavailable();
    }
    return response;
  },
  onError() {
    reportApiUnavailable();
    // Returning nothing lets the original error propagate to the caller
    // unchanged — this middleware only ever OBSERVES.
    return undefined;
  },
});
